import { DataLayer } from '@piwikpro/react-piwik-pro';
import React, { useMemo } from 'react';

import { navigate } from 'gatsby';
import { Link as GatsbyLink } from 'gatsby-link';

import { Grid2 as Grid, styled, Theme, useTheme } from '@mui/material';

import { CollectionData, ServiceSegmentsForCRM } from '@mewa/types';
import {
  BreadCrumbData,
  BreadCrumbs,
  DownloadDefaultIcon,
  DownloadHoverIcon,
  IconTextButton,
  ProductCardGridItem,
  ResponsiveGrid,
  ResponsiveProductGrid,
  ServiceTeaserProps,
} from '@mewa/ui';

import { localeBaseUrl } from '../../../../libs/alternative-links';
import Img from '../../../components/image';
import { PageHeadlineWrapper } from '../../../components/page-headline-wrapper';
import { ProductCardBaseData } from '../../../components/slider/product-card-base';
import { ProductCardColorSliderWrapper } from '../../../components/slider/product-card-color-slider-wrapper';
import { ProductCardKeyFactsWrapper } from '../../../components/slider/product-card-key-facts-wrapper';
import { jpgPath } from '../../../helpers/image-factory';
import { ConversionData } from '../../conversion/helper';
import { storeBrochures } from '../../conversion/persistence';
import imagePathDesktop from './collectionImageDesktop.png';
import imagePathMobile from './collectionImageMobile.png';

export type Collection = Omit<CollectionData, 'nomenclatures'> & {
  slug: string;
  infoText2: string | null;
  galleryTitle?: string;
  galleryIntroText?: string;
  products: ProductCardBaseData[];
};

type Props = {
  isClothingCategory: boolean;
  langPrefix: string;
  collection: Collection;
  serviceTestTeaser?: React.ReactElement<ServiceTeaserProps>;
  breadCrumbData: BreadCrumbData[];
  mobileBreadCrumbData: BreadCrumbData;
  locale: string;
  brochuresDownload: {
    text: string;
    url: string;
    preSelection: ServiceSegmentsForCRM[];
  };
  detailsPageLabel: string;
  conversion: ConversionData;
  seoFooter?: {
    html: string;
    showMoreLabel: string;
    showLessLabel: string;
  };
  'data-pagefind-meta'?: string;
};

const CollectionPage = ({
  isClothingCategory,
  langPrefix,
  collection,
  serviceTestTeaser,
  brochuresDownload,
  detailsPageLabel,
  breadCrumbData,
  mobileBreadCrumbData,
  locale,
  conversion,
  'data-pagefind-meta': dataPagefindMeta,
}: Props) => {
  const theme = useTheme();

  const DescriptionList =
    collection.description?.split('\n')?.filter((t) => t !== '') ?? [];
  const restTextList = DescriptionList.slice(1);

  const navigateToBrochuresDownload = () => {
    // Using the session storage instead of the location state makes the logic
    // with preselection & remembering a selection after a reload a bit
    // simpler.

    storeBrochures(brochuresDownload.preSelection);
    DataLayer.push({ event: 'cta_brochure_download_teaser' });
    navigate(`${langPrefix}/${brochuresDownload.url}`);
  };

  const imageData = useMemo(
    () =>
      heroImages(
        theme,
        collection.assets.keyVisualDesktop,
        collection.assets.keyVisualMobile,
      ),
    [theme, collection.assets],
  );

  return (
    <div data-pagefind-meta={dataPagefindMeta}>
      <BreadCrumbs
        data={breadCrumbData}
        mobileData={mobileBreadCrumbData}
        localeBaseUrl={localeBaseUrl(locale)}
        LinkComponent={GatsbyLink}
      />
      <PageHeadlineWrapper
        title={collection.name}
        subtitle={collection.claim}
        cta={conversion}
      />
      <ResponsiveGrid>
        <Grid size={12}>
          <HeroSection>
            <Img
              alt={collection.name}
              images={imageData}
              intendedForSearchIndex
            />
          </HeroSection>
        </Grid>
      </ResponsiveGrid>
      <ResponsiveGrid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <SubClaim>{collection.property}</SubClaim>
        </Grid>
        <Grid
          size={{
            xs: 11,
            md: 7,
            lg: 6,
          }}
        >
          {DescriptionList[0] && <IntroText>{DescriptionList[0]}</IntroText>}

          {restTextList.map((t, i) => (
            <SecondaryIntroText key={i}>{t}</SecondaryIntroText>
          ))}

          {collection.infoText2 && (
            <SecondaryIntroText>{collection.infoText2}</SecondaryIntroText>
          )}
        </Grid>
        <Grid
          offset={{ xs: 0, md: 4 }}
          size={{
            xs: 12,
            lg: 6,
            md: 8,
          }}
        >
          <ButtonOffsetContainer>
            <IconTextButton
              style={{ marginRight: 20 }}
              DefaultIcon={DownloadDefaultIcon}
              HoverIcon={DownloadHoverIcon}
              onClick={navigateToBrochuresDownload}
            >
              {brochuresDownload.text}
            </IconTextButton>
          </ButtonOffsetContainer>
        </Grid>
      </ResponsiveGrid>
      <ResponsiveProductGrid>
        {collection.products.map((product, idx) => (
          <ProductCardGridItem key={idx}>
            {isClothingCategory ? (
              <ProductCardColorSliderWrapper
                product={product}
                useMbsFallbackIcon={isClothingCategory}
                langPrefix={langPrefix}
                detailsPageLabel={detailsPageLabel}
              />
            ) : (
              <ProductCardKeyFactsWrapper
                product={product}
                useMbsFallbackIcon={isClothingCategory}
                langPrefix={langPrefix}
                detailsPageLabel={detailsPageLabel}
              />
            )}
          </ProductCardGridItem>
        ))}
      </ResponsiveProductGrid>
      {serviceTestTeaser}
    </div>
  );
};

const HeroSection = styled('div')(({ theme }) => ({
  marginBottom: '122px',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '44px',
  },
}));

const SubClaim = styled('p')(({ theme }) => ({
  fontSize: 'var(--font-size-text-md)',
  lineHeight: 'var(--line-height-text-md)',
  fontWeight: theme.typography.fontWeightMedium,
  margin: 0,
  [theme.breakpoints.down('md')]: {
    marginBottom: '50px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 'var(--font-size-text-xs)',
    lineHeight: 'var(--line-height-text-xs)',
    marginBottom: '22px',
  },
  [theme.breakpoints.up('md')]: {
    marginTop: '18px',
  },
}));

const IntroText = styled('p')(({ theme }) => ({
  ...theme.typography.h4,
  fontWeight: theme.typography.fontWeightRegular,
  margin: 0,
  // max width based on design feedback and window size of 1920xp
  maxWidth: '845px',
}));

const SecondaryIntroText = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  marginTop: '30px',
  marginBottom: 0,
  // max width based on design feedback and window size of 1920xp
  maxWidth: '845px',

  [theme.breakpoints.down('sm')]: {
    marginTop: '20px',
  },
}));

const ButtonOffsetContainer = styled('div')(({ theme }) => ({
  marginTop: '54px',
  marginLeft: '-12px',
  display: 'flex',
  alignItems: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    marginTop: '24px',
    flexDirection: 'column',
    '> button': {
      marginBottom: '5px',
    },
  },
}));

const heroImages = (
  theme: Theme,
  assetIdDesktop: string | null,
  assetIdMobile: string | null,
) => [
  {
    src: `${
      assetIdDesktop ? jpgPath(assetIdDesktop, 1720, 968) : imagePathDesktop
    }`,
    media: `(min-width: ${theme.breakpoints.values.md}px)`,
    width: 1720,
    height: 968,
  },
  {
    src: `${
      assetIdMobile ? jpgPath(assetIdMobile, 1080, 810) : imagePathMobile
    }`,
    // use hardcoded breakpoint to switch the image at the native mobile image size
    media: `(max-width: 1230px)`,
    width: 375,
    height: 281,
  },
];

export default CollectionPage;
